/*
 OK FBI — فرقة التحقيق (Squad Mode)
 Local-multiplayer investigation surface.

 Routes:
   /squad                     → Landing (create · join · reconnect)
   /squad/join                → Enter 6-digit code (public — anyone can open)
   /squad/session/:sid        → Live room (role-aware: leader | participant | guest)
   /squad/display/:sid        → Desktop / TV View-Only display

 Design:
   - Arabic RTL, dark cinematic OK FBI (Black / Gold).
   - Mobile-first for Leader + Participants + Guests.
   - Desktop 16:9 for /squad/display only (View-Only, no controls).
*/
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, formatErr } from "@/lib/api";
import {
  squadAxios, squadClient, fetchSquadSession, fetchSquadCase,
  saveSquadToken, getSquadToken, clearSquadToken, getLastSquadSession,
} from "@/lib/squadApi";
import { useAuth } from "@/pages/AuthPage";
import { assetUrl } from "@/contexts/SessionContext";
import {
  Users, Radio, KeyRound, Lock, Unlock, ChevronLeft, Play, LogOut,
  UserPlus, Monitor, CheckCircle2, XCircle, Trophy, Loader2, Copy,
  ShieldAlert, Clock, Check, RotateCcw,
} from "lucide-react";

// ─── Shared styling ──────────────────────────────────────────
const GoldText = ({ children, className = "" }) => (
  <span className={`text-[color:var(--gold,#C5A059)] ${className}`}>{children}</span>
);

const Panel = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`rounded-2xl border border-[color:var(--gold,#C5A059)]/30 bg-black/70 backdrop-blur-sm p-5 ${className}`}>{children}</div>
);

/** SuspectAvatar — never renders the browser broken-image icon.
 *  If `src` is missing OR the browser fires an onError, render the OK FBI
 *  placeholder box instead. Reused across mobile and desktop suspect surfaces. */
const SuspectAvatar = ({ src, size = "sm", testid }) => {
  const [broken, setBroken] = React.useState(false);
  // Size presets tuned for OK FBI's suspect-file aesthetic:
  //   sm  → compact list (56²)
  //   md  → SUSPECTS/INTERROGATION file card (portrait 96×120)
  //   lg  → SquadDisplay (broadcast/TV portrait 4:5, up to full column width)
  const cls = size === "lg"
    ? "w-full aspect-[4/5] rounded-md object-cover object-top bg-zinc-800 border border-zinc-800 ring-1 ring-white/5"
    : size === "md"
    ? "w-24 h-[120px] rounded-md object-cover object-top bg-zinc-800 border border-zinc-800 ring-1 ring-white/5 shrink-0"
    : "w-14 h-14 rounded object-cover bg-zinc-800";
  const placeholder = size === "lg"
    ? "w-full aspect-[4/5] bg-zinc-950 rounded-md grid place-items-center text-xs text-zinc-500 font-mono-ar tracking-[0.4em] border border-zinc-800"
    : size === "md"
    ? "w-24 h-[120px] bg-zinc-950 rounded-md grid place-items-center text-[10px] text-zinc-500 font-mono-ar tracking-[0.3em] border border-zinc-800 shrink-0"
    : "w-14 h-14 rounded bg-zinc-800 grid place-items-center text-[10px] text-zinc-500 font-mono-ar";
  // Route local `/generated_images/...` through the /api/images mount via
  // the project's canonical helper. External http(s) URLs pass through unchanged.
  const resolved = assetUrl(src);
  if (!resolved || broken) {
    return <div className={placeholder} aria-hidden data-testid={testid ? `${testid}-placeholder` : undefined}>OK FBI</div>;
  }
  return <img alt="" src={resolved} loading="lazy" className={cls} onError={() => setBroken(true)} data-testid={testid} />;
};

const PrimaryBtn = ({ children, onClick, disabled, className = "", ...props }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full min-h-[54px] rounded-xl bg-[color:var(--gold,#C5A059)] hover:brightness-110 text-black font-black tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...props}
  >{children}</button>
);

const OutlineBtn = ({ children, onClick, disabled, className = "", ...props }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full min-h-[52px] rounded-xl border border-[color:var(--gold,#C5A059)]/60 text-[color:var(--gold,#C5A059)] hover:bg-[color:var(--gold,#C5A059)]/10 font-bold tracking-wide transition-all disabled:opacity-40 ${className}`}
    {...props}
  >{children}</button>
);

const SquadShell = ({ children, title, back }) => {
  const nav = useNavigate();
  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0806] to-black text-zinc-100 font-cairo">
      <header className="sticky top-0 z-10 border-b border-[color:var(--gold,#C5A059)]/25 bg-black/85 backdrop-blur px-4 py-3 flex items-center gap-3">
        {back && (
          <button data-testid="squad-back-btn" onClick={() => nav(back)} className="p-2 -mr-2 text-zinc-400 hover:text-[color:var(--gold,#C5A059)]"><ChevronLeft className="w-6 h-6 rotate-180" /></button>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.4em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">OK FBI · SQUAD</div>
          <div className="text-lg font-bold truncate">{title || "فرقة التحقيق"}</div>
        </div>
      </header>
      <main className="p-4 pb-24 max-w-3xl mx-auto space-y-4">{children}</main>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SquadLanding  —  create / join / reconnect
// ═══════════════════════════════════════════════════════════════
export function SquadLanding() {
  const nav = useNavigate();
  const { user, loaded } = useAuth();
  const [cases, setCases] = useState([]);
  const [pickedCase, setPickedCase] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(getLastSquadSession());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cases");
        setCases(Array.isArray(data) ? data : []);
        if (data?.[0]?.id) setPickedCase(data[0].id);
      } catch (e) { console.debug("squad landing cases err", e); }
    })();
  }, []);

  const create = async () => {
    if (!user) {
      toast.error("سجّل دخولك أولاً لإنشاء فرقة تحقيق");
      nav("/login?next=/squad");
      return;
    }
    if (!pickedCase) { toast.error("اختر القضية أولاً"); return; }
    setBusy(true);
    try {
      const { data } = await squadAxios.post("/sessions/create", { case_id: pickedCase });
      saveSquadToken(data.session.session_id, data.leader_token, "leader", data.leader_participant_id);
      toast.success("تم إنشاء فرقة التحقيق");
      nav(`/squad/session/${data.session.session_id}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "فشل الإنشاء");
    } finally { setBusy(false); }
  };

  const reconnect = () => {
    if (!last) return;
    const rec = getSquadToken(last);
    if (!rec) { clearSquadToken(last); setLast(null); return; }
    nav(`/squad/session/${last}`);
  };

  return (
    <SquadShell title="فرقة التحقيق" back="/app">
      <Panel data-testid="squad-landing-intro">
        <div className="text-[color:var(--gold,#C5A059)] text-sm font-bold mb-2">لعب جماعي محلي</div>
        <p className="text-zinc-300 leading-relaxed text-sm">
          العَب مع عائلتك أو أصدقائك في نفس المكان. القائد يتحكم من جهازه، والباقي ينضمّون بحساباتهم أو كضيوف.
        </p>
      </Panel>

      {last && (
        <Panel className="border-emerald-500/40">
          <div className="text-emerald-400 text-xs mb-2">لديك جلسة سابقة نشطة</div>
          <OutlineBtn data-testid="squad-reconnect-btn" onClick={reconnect}>
            العودة إلى الجلسة السابقة
          </OutlineBtn>
        </Panel>
      )}

      <Panel>
        <div className="text-lg font-bold mb-3">إنشاء فرقة</div>
        <label className="block text-xs text-zinc-400 mb-2">اختر القضية</label>
        <select
          data-testid="squad-case-picker"
          value={pickedCase}
          onChange={(e) => setPickedCase(e.target.value)}
          className="w-full min-h-[48px] rounded-lg bg-zinc-900 border border-[color:var(--gold,#C5A059)]/30 px-3 text-zinc-100 mb-4"
        >
          <option value="">— اختر —</option>
          {cases.map((c) => (
            <option key={c.id} value={c.id}>{c.title || c.code || c.id}</option>
          ))}
        </select>
        <PrimaryBtn data-testid="squad-create-btn" onClick={create} disabled={busy || !pickedCase}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin inline" /> : "إنشاء الفرقة"}
        </PrimaryBtn>
      </Panel>

      <Panel>
        <div className="text-lg font-bold mb-2">الانضمام لفرقة موجودة</div>
        <p className="text-zinc-400 text-sm mb-4">أدخل رمز الجلسة المكوّن من 6 أرقام أو امسح رمز QR.</p>
        <PrimaryBtn data-testid="squad-join-nav-btn" onClick={() => nav("/squad/join")}>
          <UserPlus className="w-5 h-5 inline ml-2" /> انضمام إلى فرقة
        </PrimaryBtn>
      </Panel>

      <div className="text-[10px] tracking-[0.35em] text-zinc-500 text-center mt-8 font-mono-ar">
        لعب محلي · لا يؤثر على النقاط العالمية
      </div>
    </SquadShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// SquadJoin  —  Single-screen join (Name + 6-digit code)
// ═══════════════════════════════════════════════════════════════
export function SquadJoin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialCode = (params.get("code") || "").replace(/\D/g, "").slice(0, 6);
  const { user } = useAuth();
  const [code, setCode] = useState(initialCode);
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);

  const joinAccount = async () => {
    if (code.length !== 6) { toast.error("الرمز يجب أن يكون 6 أرقام"); return; }
    setBusy(true);
    try {
      const { data } = await squadAxios.post("/join/account", { join_code: code });
      saveSquadToken(data.session.session_id, data.token, data.role, data.participant_id);
      toast.success("انضممت للفرقة");
      nav(`/squad/session/${data.session.session_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "فشل الانضمام");
    } finally { setBusy(false); }
  };

  const joinGuest = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const nm = guestName.trim();
    if (!nm) { toast.error("أدخل اسمك"); return; }
    if (code.length !== 6) { toast.error("الرمز يجب أن يكون 6 أرقام"); return; }
    setBusy(true);
    try {
      const { data } = await squadAxios.post("/join/guest", { join_code: code, name: nm });
      saveSquadToken(data.session.session_id, data.token, data.role, data.participant_id);
      toast.success(`مرحباً ${nm}`);
      nav(`/squad/session/${data.session.session_id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "فشل الانضمام");
    } finally { setBusy(false); }
  };

  const canSubmit = !busy && guestName.trim().length > 0 && code.length === 6;

  return (
    <SquadShell title="دخول Squad" back="/squad">
      <Panel>
        <form onSubmit={joinGuest}>
          <label className="block text-sm text-zinc-300 mb-2">الاسم</label>
          <input
            data-testid="squad-guest-name-input"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value.slice(0, 60))}
            placeholder="الاسم"
            className="w-full h-14 px-4 bg-zinc-900 border border-[color:var(--gold,#C5A059)]/40 rounded-lg text-lg mb-4 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[color:var(--gold,#C5A059)]"
            autoFocus
          />

          <label className="block text-sm text-zinc-300 mb-2">رقم الفرقة / رقم الجلسة</label>
          <input
            data-testid="squad-code-input"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            className="w-full h-16 text-center text-3xl tracking-[0.6em] font-mono-ar bg-zinc-900 border border-[color:var(--gold,#C5A059)]/40 rounded-lg text-[color:var(--gold,#C5A059)] placeholder:text-zinc-700 mb-4 focus:outline-none focus:border-[color:var(--gold,#C5A059)]"
          />

          <PrimaryBtn
            type="submit"
            data-testid="squad-code-continue"
            onClick={joinGuest}
            disabled={!canSubmit}
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin inline" /> : "متابعة"}
          </PrimaryBtn>

          <div className="text-[10px] text-zinc-500 mt-3 text-center">
            الضيف لا يحصل على حساب دائم · لا رتب · لا نقاط عالمية
          </div>

          {user && (
            <div className="mt-4 pt-3 border-t border-zinc-800/80 text-center">
              <button
                type="button"
                data-testid="squad-join-account-btn"
                onClick={joinAccount}
                disabled={busy || code.length !== 6}
                className="text-xs text-[color:var(--gold,#C5A059)]/80 hover:text-[color:var(--gold,#C5A059)] hover:underline transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                الدخول بحسابي ({user.name || user.agent_id})
              </button>
            </div>
          )}
        </form>
      </Panel>
    </SquadShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// SquadRoom  —  Live room (Lobby + Play + Results). Role-aware.
// ═══════════════════════════════════════════════════════════════
const POLL_MS = 3500;

function useSquadPoll(sessionId) {
  const [snap, setSnap] = useState(null);
  const [caseSnap, setCaseSnap] = useState(null);
  const [err, setErr] = useState(null);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const s = await fetchSquadSession(sessionId);
      setSnap(s);
      if (s?.session?.state && s.session.state !== "LOBBY") {
        try { const c = await fetchSquadCase(sessionId); setCaseSnap(c); } catch {}
      }
      setErr(null);
    } catch (e) {
      setErr(e?.response?.status || "error");
    } finally { inFlight.current = false; }
  }, [sessionId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  return { snap, caseSnap, err, refresh };
}

const StateBadge = ({ state }) => {
  const map = {
    LOBBY:      { text: "قيد التجهيز",   cls: "bg-zinc-800 text-zinc-300 border-zinc-600" },
    ACTIVE:     { text: "التحقيق جارٍ",  cls: "bg-emerald-950 text-emerald-300 border-emerald-500/40" },
    FINAL_VOTE: { text: "التصويت النهائي", cls: "bg-amber-950 text-amber-300 border-amber-500/40" },
    COMPLETED:  { text: "انتهت",         cls: "bg-blue-950 text-blue-300 border-blue-500/40" },
    CLOSED:     { text: "مغلقة",         cls: "bg-red-950 text-red-300 border-red-500/40" },
  };
  const m = map[state] || map.LOBBY;
  return <span className={`inline-block px-2 py-1 rounded text-[10px] tracking-wider border ${m.cls}`}>{m.text}</span>;
};

// Alibi status: cases either use Arabic phrases directly (built-in cases) or
// English enums (some AI-generated custom cases). Map known English enums to
// Arabic; if the value is already Arabic (or anything else), show it as-is.
const ALIBI_STATUS_AR = {
  confirmed: "مؤكدة",
  verified: "مؤكدة",
  unconfirmed: "غير مؤكدة",
  unverified: "غير مؤكدة",
  contradicted: "متناقضة",
  contradictory: "متناقضة",
  weak: "ضعيفة",
  partial: "مؤكدة جزئياً",
  partially_confirmed: "مؤكدة جزئياً",
  lying: "كاذبة",
  false: "كاذبة",
};
function translateAlibiStatus(s) {
  if (!s) return "";
  const key = String(s).trim().toLowerCase();
  return ALIBI_STATUS_AR[key] || String(s);
}
// Timeline entries can arrive as strings, arrays [time, event] (from Python tuples),
// or objects {time, event/action}. Normalise for display without inventing content.
function fmtTimelineEntry(t) {
  if (t == null) return { time: "", event: "" };
  if (typeof t === "string") return { time: "", event: t };
  if (Array.isArray(t)) return { time: t[0] || "", event: t[1] || "" };
  return { time: t.time || t.at || "", event: t.event || t.action || t.desc || "" };
}
// Question Card for a single interrogation prompt. When `confront` maps to an
// evidence entry we surface the *real name* of the evidence (never the raw ID).
// Locked evidence yields a safe hint instead of leaking its contents.
function QuestionCard({ index, question, confrontEv, testidBase }) {
  const [open, setOpen] = useState(false);
  const hasConfront = !!confrontEv;
  // Locked = server sanitized the payload → no description means fully locked.
  // (evidence_has_pin + not-yet-unlocked path in squad_routes.py)
  const isLocked = !!(confrontEv && confrontEv.pin_locked && !confrontEv.description);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/70" data-testid={`${testidBase}-card-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full text-right p-3 flex items-start gap-3 hover:bg-zinc-900/70 transition-colors"
        aria-expanded={open}
        data-testid={`${testidBase}-toggle-${index}`}
      >
        <span className="mt-0.5 w-7 h-7 rounded-full grid place-items-center bg-[color:var(--gold,#C5A059)] text-black text-xs font-black shrink-0">{index + 1}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-zinc-100 leading-relaxed">{question}</span>
          {hasConfront && (
            <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-950/30 text-[10px] text-amber-300 tracking-wide" data-testid={`${testidBase}-badge-${index}`}>
              {isLocked ? <Lock className="w-3 h-3" /> : <span aria-hidden>🔎</span>}
              <span>{isLocked ? "دليل مقفل مرتبط بالسؤال" : "دليل متاح للمواجهة"}</span>
            </span>
          )}
        </span>
        <span className="text-[10px] text-[color:var(--gold,#C5A059)] tracking-widest shrink-0 mt-1">{open ? "إغلاق" : "فتح"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-zinc-800/60" data-testid={`${testidBase}-body-${index}`}>
          {hasConfront ? (
            isLocked ? (
              <div className="rounded border border-amber-500/30 bg-amber-950/20 p-2 mt-2">
                <div className="text-[11px] text-amber-300 tracking-widest mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> مواجهة مقفلة</div>
                <div className="text-xs text-zinc-200">يوجد دليل مرتبط بهذا السؤال، لكنه لم يُفتح بعد.</div>
                <div className="text-[10px] text-zinc-400 mt-1">افتح الدليل أولاً من قسم الأدلة لاستخدامه في المواجهة.</div>
              </div>
            ) : (
              <div className="rounded border border-emerald-500/30 bg-emerald-950/10 p-2 mt-2" data-testid={`${testidBase}-confront-${index}`}>
                <div className="text-[11px] text-emerald-300 tracking-widest mb-1">واجه المشتبه بالدليل</div>
                <div className="text-sm font-bold text-zinc-100" data-testid={`${testidBase}-confront-name-${index}`}>{confrontEv.name || "دليل"}</div>
                {confrontEv.type && <div className="text-[10px] text-zinc-500 mt-0.5">النوع: {confrontEv.type}</div>}
                {confrontEv.image && (
                  <img
                    alt=""
                    src={assetUrl(confrontEv.image)}
                    loading="lazy"
                    className="mt-2 rounded max-h-40 object-cover w-full bg-zinc-800"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                {confrontEv.description && (
                  <div className="text-xs text-zinc-300 mt-2 leading-relaxed">{confrontEv.description}</div>
                )}
              </div>
            )
          ) : (
            <div className="text-[11px] text-zinc-500 mt-2">سؤال مفتوح — استخدم إجابة المشتبه لبناء استنتاجك.</div>
          )}
        </div>
      )}
    </div>
  );
}

function MembersList({ members, role, leaderPid, onKick }) {
  return (
    <div className="space-y-2" data-testid="squad-members-list">
      {members.map((m) => {
        const isLeader = m.participant_id === leaderPid;
        return (
          <div
            key={m.participant_id}
            data-testid={`squad-member-${m.participant_id}`}
            className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2"
          >
            <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold ${isLeader ? "bg-[color:var(--gold,#C5A059)] text-black" : m.is_guest ? "bg-zinc-700 text-zinc-200" : "bg-emerald-900 text-emerald-200"}`}>
              {(m.display_name || "?").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate font-semibold text-sm">{m.display_name}{m.agent_id ? ` · #${m.agent_id}` : ""}</div>
              <div className="text-[10px] text-zinc-500">
                {isLeader ? "قائد الفرقة" : m.is_guest ? "ضيف" : "محقق"}
              </div>
            </div>
            {role === "leader" && !isLeader && (
              <button
                data-testid={`squad-kick-${m.participant_id}`}
                onClick={() => onKick(m.participant_id)}
                className="text-xs text-red-400 hover:text-red-300 px-2 py-1"
              >إزالة</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function QrPanel({ sessionId, joinCode }) {
  const joinUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/squad/join?code=${joinCode}`;
  }, [joinCode]);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <Panel data-testid="squad-qr-panel">
      <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
        <div className="bg-white p-2 rounded-lg" data-testid="squad-qr">
          <QRCodeSVG value={joinUrl} size={110} level="M" />
        </div>
        <div>
          <div className="text-xs text-zinc-400 mb-1">رمز الانضمام</div>
          <div data-testid="squad-join-code" className="text-4xl font-black tracking-[0.5em] text-[color:var(--gold,#C5A059)] leading-none">{joinCode}</div>
          <div className="text-[10px] text-zinc-500 mt-2 truncate">{joinUrl}</div>
          <button data-testid="squad-copy-link" onClick={copy} className="mt-2 text-xs text-[color:var(--gold,#C5A059)] hover:brightness-125 flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> {copied ? "تم النسخ" : "نسخ الرابط"}
          </button>
        </div>
      </div>
    </Panel>
  );
}

function EvidenceItem({ ev, sessionId, role, onUnlocked, disabledUnlock }) {
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(null);

  const submit = async () => {
    const p = pin.trim();
    if (!p) return;
    setBusy(true);
    setErr("");
    try {
      const c = squadClient(sessionId);
      const { data } = await c.post(`/sessions/${sessionId}/evidence/${ev.id}/unlock`, { pin: p });
      if (data.ok) {
        toast.success("تم فتح الدليل");
        setShowPin(false);
        setPin("");
        onUnlocked?.();
      } else {
        setErr(data.error || "الرمز غير صحيح");
        setRemaining(data.remaining_attempts);
      }
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (s === 429) setErr(typeof d === "string" ? d : "محاولات كثيرة — انتظر قليلاً");
      else setErr(typeof d === "string" ? d : "فشل الإرسال");
    } finally { setBusy(false); }
  };

  if (ev.pin_locked && !ev.description && !ev.image) {
    // Locked view (server never sent secret content).
    return (
      <div data-testid={`squad-ev-${ev.id}`} className="rounded-lg border border-amber-600/30 bg-amber-950/20 p-3">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4 text-amber-400" />
          <div className="font-bold text-amber-200">{ev.name || "دليل مقفول"}</div>
        </div>
        {ev.pin_hint && (
          <div className="text-xs text-zinc-400 mb-2">تلميح: {ev.pin_hint}</div>
        )}
        {!showPin ? (
          <button
            data-testid={`squad-ev-${ev.id}-open`}
            onClick={() => setShowPin(true)}
            disabled={disabledUnlock}
            className="text-xs text-amber-300 hover:text-amber-200 disabled:opacity-40"
          >
            <KeyRound className="w-3.5 h-3.5 inline ml-1" /> إدخال رمز الدخول
          </button>
        ) : (
          <div className="mt-2">
            <input
              data-testid={`squad-ev-${ev.id}-pin-input`}
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.slice(0, 12))}
              placeholder="_ _ _ _"
              className="w-full h-12 text-center text-xl tracking-[0.5em] bg-zinc-900 border border-amber-500/40 rounded"
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
            {err && (
              <div className="text-xs text-red-400 mt-1" data-testid={`squad-ev-${ev.id}-error`}>
                {err}{remaining != null && remaining >= 0 ? ` · محاولات متبقية: ${remaining}` : ""}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <button
                data-testid={`squad-ev-${ev.id}-pin-submit`}
                onClick={submit}
                disabled={busy || !pin.trim()}
                className="flex-1 py-2 rounded bg-amber-500 text-black font-bold text-sm disabled:opacity-40"
              >{busy ? "..." : "فتح"}</button>
              <button
                onClick={() => { setShowPin(false); setPin(""); setErr(""); }}
                className="flex-1 py-2 rounded border border-zinc-600 text-zinc-300 text-sm"
              >إلغاء</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Unlocked or never locked
  return (
    <div data-testid={`squad-ev-${ev.id}`} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
      <div className="flex items-center gap-2 mb-1">
        {ev.pin_locked && <Unlock className="w-4 h-4 text-emerald-400" />}
        <div className="font-bold">{ev.name}</div>
      </div>
      {ev.description && <div className="text-sm text-zinc-300 mt-1 leading-relaxed">{ev.description}</div>}
      {ev.image && (
        <img
          alt=""
          src={assetUrl(ev.image)}
          loading="lazy"
          className="mt-2 rounded max-h-48 object-cover w-full bg-zinc-800"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
    </div>
  );
}

function LeaderControls({ sessionId, snap, caseSnap, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [voting, setVoting] = useState(false);
  const [chosenSuspect, setChosenSuspect] = useState("");
  const [showAccuse, setShowAccuse] = useState(false);
  const [pairing, setPairing] = useState(null);
  const s = snap?.session;

  const call = async (action) => {
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      await c.post(`/sessions/${sessionId}/${action}`);
      onRefresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "فشل الأمر");
    } finally { setBusy(false); }
  };

  const startVote = async () => {
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      await c.post(`/sessions/${sessionId}/vote/start`, { prompt: "من المشتبه به الرئيسي؟" });
      toast.success("بدأ التصويت");
      onRefresh();
    } catch (e) { toast.error("فشل بدء التصويت"); } finally { setBusy(false); }
  };
  const closeVote = async () => {
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      await c.post(`/sessions/${sessionId}/vote/close`);
      toast.success("تم إغلاق التصويت");
      onRefresh();
    } catch (e) { toast.error("فشل الإغلاق"); } finally { setBusy(false); }
  };
  const [confirmingAccuse, setConfirmingAccuse] = useState(false);
  // Phase 1: leader-only round reset → POST /api/squad/sessions/{id}/reset
  // (re-locks PIN evidence, clears ballots/scoreboard, back to INTRO).
  const [confirmReset, setConfirmReset] = useState(false);
  const resetRound = async () => {
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      await c.post(`/sessions/${sessionId}/reset`);
      toast.success("تم تصفير الجولة — عادت القضية للبداية");
      onRefresh();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل التصفير"); }
    finally { setBusy(false); setConfirmReset(false); }
  };
  const submitAccuse = async () => {
    if (!chosenSuspect) { toast.error("اختر المشتبه به"); return; }
    if (busy) return; // hard guard against double-submit
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      const { data } = await c.post(`/sessions/${sessionId}/accuse`, { suspect_id: chosenSuspect });
      toast.success(data?.result?.solved ? "أحسنتم! قرار صحيح" : "قرار غير صحيح");
      setShowAccuse(false);
      setConfirmingAccuse(false);
      onRefresh();
    } catch (e) { toast.error("فشل الاعتماد"); } finally { setBusy(false); }
  };

  const pairDisplay = async () => {
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      // Backend route: POST /api/squad/display/pair (leader-only).
      // squadClient baseURL is already ${API}/api/squad, so the client-relative
      // path is just `/display/pair` — NOT `/sessions/display/pair`.
      const { data } = await c.post(`/display/pair`);
      const displayUrl = `${window.location.origin}/squad/display/${sessionId}?dt=${encodeURIComponent(data.display_token)}`;
      setPairing(displayUrl);
    } catch (e) { toast.error("فشل ربط الشاشة"); } finally { setBusy(false); }
  };

  const closeSession = async () => {
    if (!window.confirm("هل تريد إنهاء جلسة الفرقة؟ لا يمكن التراجع.")) return;
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      await c.post(`/sessions/${sessionId}/close`);
      clearSquadToken(sessionId);
      toast.success("تم إغلاق الجلسة");
      window.location.href = "/squad";
    } catch (e) { toast.error("فشل الإغلاق"); } finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <>
      {s.state === "LOBBY" && (
        <PrimaryBtn data-testid="squad-leader-start-btn" onClick={() => call("start")} disabled={busy}>
          <Play className="w-5 h-5 inline ml-2" /> بدء التحقيق
        </PrimaryBtn>
      )}

      {s.state === "ACTIVE" && (() => {
        const stage = s.sub_stage || "INTRO";
        const advanceStage = async (next) => {
          setBusy(true);
          try {
            const c = squadClient(sessionId);
            await c.post(`/sessions/${sessionId}/stage`, { stage: next });
            onRefresh();
          } catch (e) { toast.error("فشل الانتقال للمرحلة"); }
          finally { setBusy(false); }
        };
        return (
          <>
            <div className="text-[10px] text-zinc-500 text-center px-1 py-1" data-testid="squad-leader-stage-hint">
              مرحلة القائد: {stage === "INTRO" ? "المقدمة" : stage === "EVIDENCE" ? "الأدلة" : stage === "SUSPECTS" ? "المشتبه بهم" : stage === "INTERROGATION" ? "الاستجواب" : stage === "VOTING" ? "التصويت" : "الاتهام"}
            </div>
            {stage === "INTRO" && (
              <PrimaryBtn data-testid="squad-leader-next-evidence" onClick={() => advanceStage("EVIDENCE")} disabled={busy}>
                استعراض الأدلة
              </PrimaryBtn>
            )}
            {stage === "EVIDENCE" && (
              <PrimaryBtn data-testid="squad-leader-next-suspects" onClick={() => advanceStage("SUSPECTS")} disabled={busy}>
                الانتقال للمشتبه بهم
              </PrimaryBtn>
            )}
            {stage === "SUSPECTS" && (
              <PrimaryBtn data-testid="squad-leader-next-interrogation" onClick={() => advanceStage("INTERROGATION")} disabled={busy}>
                بدء الاستجواب
              </PrimaryBtn>
            )}
            {stage === "INTERROGATION" && (
              <PrimaryBtn data-testid="squad-leader-next-voting" onClick={async () => { await advanceStage("VOTING"); await startVote(); }} disabled={busy}>
                بدء تصويت الفرقة
              </PrimaryBtn>
            )}
            {stage === "VOTING" && (
              <>
                {s.vote_open ? (
                  <OutlineBtn data-testid="squad-leader-close-vote-btn" onClick={closeVote} disabled={busy}>
                    إغلاق التصويت
                  </OutlineBtn>
                ) : null}
                <PrimaryBtn data-testid="squad-leader-next-accuse" onClick={() => advanceStage("ACCUSING")} disabled={busy}>
                  الانتقال للاتهام النهائي
                </PrimaryBtn>
              </>
            )}
            {stage === "ACCUSING" && (
              <PrimaryBtn data-testid="squad-leader-accuse-btn" onClick={() => setShowAccuse(true)} disabled={busy}>
                فتح لوحة الاتهام
              </PrimaryBtn>
            )}
          </>
        );
      })()}

      <OutlineBtn data-testid="squad-leader-pair-display-btn" onClick={pairDisplay} disabled={busy}>
        <Monitor className="w-4 h-4 inline ml-2" /> ربط شاشة العرض
      </OutlineBtn>

      {pairing && (
        <Panel className="border-emerald-500/40">
          <div className="text-xs text-zinc-400 mb-2">افتح هذا الرابط على شاشة الديسكتوب/التلفاز:</div>
          <div className="p-2 bg-zinc-900 rounded font-mono text-[10px] break-all mb-2">{pairing}</div>
          <div className="bg-white p-2 rounded inline-block">
            <QRCodeSVG value={pairing} size={110} level="M" />
          </div>
        </Panel>
      )}

      {/* Danger action: only show while the case is still in early stages so it
          never sits next to the accuse CTA. Hidden during ACCUSING/COMPLETED. */}
      {(s.state === "LOBBY" || ["INTRO","EVIDENCE","SUSPECTS","INTERROGATION"].includes(s.sub_stage || "INTRO")) && (
        <>
          {/* Round reset only while ACTIVE — in LOBBY the backend would
              otherwise jump the session straight to ACTIVE/INTRO, bypassing start. */}
          {s.state === "ACTIVE" && (
            !confirmReset ? (
              <OutlineBtn data-testid="squad-leader-reset-round-btn" onClick={() => setConfirmReset(true)} disabled={busy} className="border-red-500/40 text-red-400/80">
                <RotateCcw className="w-4 h-4 inline ml-2" /> إعادة تعيين الجولة
              </OutlineBtn>
            ) : (
              <Panel className="border-red-500/40">
                <div className="text-xs text-zinc-400 mb-2">سيتم تصفير تقدم الجولة (الأدلة، التصويت، النتيجة) والعودة للبداية.</div>
                <div className="flex gap-2">
                  <button data-testid="squad-leader-reset-round-confirm" onClick={resetRound} disabled={busy}
                    className="flex-1 py-2 rounded bg-red-600 text-white font-bold text-sm disabled:opacity-40">تأكيد التصفير</button>
                  <button data-testid="squad-leader-reset-round-cancel" onClick={() => setConfirmReset(false)} disabled={busy}
                    className="flex-1 py-2 rounded border border-zinc-600 text-zinc-300 text-sm">تراجع</button>
                </div>
              </Panel>
            )
          )}
          <OutlineBtn data-testid="squad-leader-close-session-btn" onClick={closeSession} disabled={busy} className="border-red-500/40 text-red-400/80">
            <LogOut className="w-4 h-4 inline ml-2" /> إنهاء الجلسة
          </OutlineBtn>
        </>
      )}

      {showAccuse && caseSnap && (
        <AccusationModal
          caseSnap={caseSnap}
          chosenSuspect={chosenSuspect}
          setChosenSuspect={setChosenSuspect}
          confirming={confirmingAccuse}
          setConfirming={setConfirmingAccuse}
          busy={busy}
          onSubmit={submitAccuse}
          onClose={() => { if (!busy) { setShowAccuse(false); setConfirmingAccuse(false); } }}
        />
      )}
    </>
  );
}

// Full-screen accusation modal — two-step: (1) pick suspect via card grid,
// then (2) confirm before hitting POST /accuse. Confirmation is REQUIRED so a
// single mistap doesn't send the final decision, and busy-flag hard-guards
// against double submits.
function AccusationModal({ caseSnap, chosenSuspect, setChosenSuspect, confirming, setConfirming, busy, onSubmit, onClose }) {
  const chosen = React.useMemo(() =>
    (caseSnap.suspects || []).find(s => s.id === chosenSuspect) || null,
    [caseSnap, chosenSuspect]
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="squad-accuse-modal">
      <div className="min-h-full flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-4xl bg-zinc-950 border border-amber-500/40 rounded-xl shadow-[0_20px_80px_-20px_rgba(197,160,89,0.4)]">
          {!confirming ? (
            <>
              <div className="p-5 border-b border-zinc-800">
                <div className="text-[10px] tracking-[0.4em] text-[color:var(--gold,#C5A059)] font-mono-ar">قرار نهائي</div>
                <div className="text-2xl font-black text-zinc-50 mt-1">الاتهام النهائي</div>
                <div className="text-sm text-zinc-400 mt-2">اختاروا المشتبه الذي تعتقد الفرقة أنه المسؤول عن الجريمة. هذا القرار سيكشف نتيجة القضية ولن يمكن تغييره.</div>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(caseSnap.suspects || []).map((s) => {
                    const selected = chosenSuspect === s.id;
                    return (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => setChosenSuspect(s.id)}
                        className={`text-right rounded-lg border p-3 flex items-start gap-3 transition-all ${
                          selected
                            ? "border-[color:var(--gold,#C5A059)] bg-[color:var(--gold,#C5A059)]/10 shadow-[0_0_0_1px_var(--gold,#C5A059)]"
                            : "border-zinc-800 bg-zinc-900/60 hover:border-[color:var(--gold,#C5A059)]/40 hover:bg-zinc-900"
                        }`}
                        data-testid={`squad-accuse-card-${s.id}`}
                        aria-pressed={selected}
                      >
                        <SuspectAvatar src={s.portrait} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">مشتبه به</div>
                          <div className="font-bold text-base leading-tight mt-0.5 text-zinc-50 truncate" title={s.name}>{s.name}</div>
                          {(s.job || s.age) && (
                            <div className="text-xs text-zinc-400 mt-1">
                              {s.job || ""}{s.job && s.age ? " · " : ""}{s.age ? `${s.age}` : ""}
                            </div>
                          )}
                        </div>
                        {selected && <Check className="w-5 h-5 text-[color:var(--gold,#C5A059)] shrink-0 mt-1" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
                {chosen && (
                  <div className="mt-4 p-3 rounded border border-[color:var(--gold,#C5A059)]/30 bg-[color:var(--gold,#C5A059)]/5 text-sm" data-testid="squad-accuse-selected-summary">
                    <span className="text-zinc-500">المشتبه المختار:</span> <span className="text-zinc-50 font-bold" data-testid="squad-accuse-selected-name">{chosen.name}</span>
                  </div>
                )}
                <div className="mt-4 flex flex-col-reverse md:flex-row gap-2 md:justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="px-4 py-2 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-900 text-sm"
                    data-testid="squad-accuse-cancel"
                  >إلغاء</button>
                  <PrimaryBtn
                    data-testid="squad-accuse-submit"
                    onClick={() => setConfirming(true)}
                    disabled={!chosenSuspect || busy}
                  >اعتماد الاتهام النهائي</PrimaryBtn>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6" data-testid="squad-accuse-confirm-view">
              <div className="text-[10px] tracking-[0.4em] text-red-400 font-mono-ar">لا رجعة</div>
              <div className="text-2xl font-black text-zinc-50 mt-1">تأكيد الاتهام النهائي</div>
              <div className="text-sm text-zinc-400 mt-2">اخترتم: <span className="text-zinc-50 font-bold" data-testid="squad-accuse-confirm-name">{chosen?.name}</span></div>
              <div className="text-xs text-zinc-500 mt-1">بعد تأكيد الاتهام ستُكشف نتيجة القضية، ولن يمكن تغيير القرار.</div>
              <div className="mt-6 flex flex-col-reverse md:flex-row gap-2 md:justify-end">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="px-4 py-2 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-900 text-sm"
                  data-testid="squad-accuse-back"
                >العودة للتحقيق</button>
                <PrimaryBtn
                  data-testid="squad-accuse-confirm-btn"
                  onClick={onSubmit}
                  disabled={busy}
                >{busy ? "جارٍ اعتماد الاتهام..." : "تأكيد الاتهام"}</PrimaryBtn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m && r) return `${m} د ${r} ث`;
  if (m) return `${m} د`;
  return `${r} ث`;
}

// Session-scoped scoreboard: shows every participant's individual score for
// THIS squad session only. Never rendered from client-calculated values —
// the backend is authoritative. Rows are pre-sorted (score desc, tie-broken
// by pin solves then correct vote then name).
function SessionScoreboard({ scoreboard, mvpIds = [], compact = false, testidBase = "squad-scoreboard" }) {
  if (!Array.isArray(scoreboard) || scoreboard.length === 0) return null;
  const mvpSet = new Set(mvpIds || []);
  const medal = (i) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`);
  return (
    <div data-testid={testidBase} className="space-y-2">
      {scoreboard.map((row, i) => {
        const isMvp = mvpSet.has(row.participant_id);
        return (
          <details
            key={row.participant_id}
            className={`rounded-lg border p-3 ${isMvp ? "border-[color:var(--gold,#C5A059)] bg-[color:var(--gold,#C5A059)]/5" : "border-zinc-800 bg-zinc-900/60"}`}
            data-testid={`${testidBase}-row-${i}`}
          >
            <summary className="flex items-center gap-3 cursor-pointer list-none">
              <div className="text-xl w-8 text-center" aria-hidden>{medal(i)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-zinc-50 truncate" title={row.display_name}>{row.display_name}</span>
                  {isMvp && (
                    <span className="text-[9px] tracking-[0.4em] px-2 py-0.5 rounded-full border border-[color:var(--gold,#C5A059)] text-[color:var(--gold,#C5A059)] font-mono-ar" data-testid={`${testidBase}-mvp-badge`}>MVP</span>
                  )}
                </div>
                {!compact && row.role && (
                  <div className="text-[10px] text-zinc-500">{row.role === "leader" ? "قائد الفرقة" : row.role === "guest" ? "ضيف" : "محقق"}</div>
                )}
              </div>
              <div className="text-left shrink-0">
                <div className="text-lg font-black text-[color:var(--gold,#C5A059)] font-mono-ar" data-testid={`${testidBase}-score-${i}`}>{row.session_score}</div>
                <div className="text-[9px] tracking-[0.3em] text-zinc-500">نقاط الجلسة</div>
              </div>
            </summary>
            {row.events && row.events.length > 0 && (
              <ul className="mt-2 pt-2 border-t border-zinc-800/60 space-y-1 text-xs text-zinc-300">
                {row.events.map((ev, ei) => (
                  <li key={ei} className="flex items-center justify-between gap-2">
                    <span>{ev.label || "مساهمة"}{ev.evidence_name ? ` — ${ev.evidence_name}` : ""}</span>
                    <span className="text-[color:var(--gold,#C5A059)] font-bold shrink-0">+{ev.points}</span>
                  </li>
                ))}
              </ul>
            )}
          </details>
        );
      })}
      <div className="text-[10px] text-zinc-500 mt-2 text-center">
        نقاط الجلسة داخلية · لا تؤثر على XP أو الرتب العالمية
      </div>
    </div>
  );
}

function ResultsPanel({ result }) {
  if (!result) return null;
  return (
    <Panel data-testid="squad-result-panel" className={result.solved ? "border-emerald-500/50" : "border-red-500/50"}>
      <div className="text-center mb-4">
        {result.solved ? (
          <>
            <Trophy className="w-10 h-10 mx-auto text-[color:var(--gold,#C5A059)]" />
            <div className="text-lg font-black mt-1 text-emerald-300">قرار صحيح</div>
          </>
        ) : (
          <>
            <XCircle className="w-10 h-10 mx-auto text-red-400" />
            <div className="text-lg font-black mt-1 text-red-300">قرار غير صحيح</div>
          </>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-2 text-center" data-testid="squad-result-chosen">
          <div className="text-[9px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar mb-1">اختيار الفرقة</div>
          <div className="mx-auto w-fit"><SuspectAvatar src={result.chosen_suspect_portrait} size="md" /></div>
          <div className="text-sm font-bold text-zinc-50 mt-2" data-testid="squad-result-chosen-name">{result.chosen_suspect_name || "—"}</div>
          {result.chosen_suspect_job && <div className="text-[11px] text-zinc-400">{result.chosen_suspect_job}</div>}
        </div>
        {!result.solved && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/10 p-2 text-center" data-testid="squad-result-correct">
            <div className="text-[9px] tracking-[0.35em] text-emerald-300 font-mono-ar mb-1">المشتبه الصحيح</div>
            <div className="mx-auto w-fit"><SuspectAvatar src={result.correct_suspect_portrait} size="md" /></div>
            <div className="text-sm font-bold text-zinc-50 mt-2" data-testid="squad-result-correct-name">{result.correct_suspect_name || "—"}</div>
            {result.correct_suspect_job && <div className="text-[11px] text-zinc-400">{result.correct_suspect_job}</div>}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-zinc-500">نقاط الفرقة:</span> <GoldText className="font-bold">{result.squad_score}</GoldText></div>
        <div><span className="text-zinc-500">مدة التحقيق:</span> {fmtDuration(result.elapsed_seconds)}</div>
        <div><span className="text-zinc-500">الأدلة المفتوحة:</span> {result.evidence_unlocked}</div>
        <div><span className="text-zinc-500">أدلة مقفولة حُلّت:</span> {result.pin_solved}</div>
        <div><span className="text-zinc-500">محاولات خاطئة:</span> {result.wrong_pin_attempts}</div>
        <div><span className="text-zinc-500">اتفاق الفرقة:</span> {Math.round((result.agreement_ratio||0)*100)}%</div>
      </div>
      {(result.motive || result.method || result.key_contradiction) && (
        <div className="mt-4 border-t border-zinc-800/60 pt-3 space-y-2 text-sm" data-testid="squad-result-explanation">
          <div className="text-[10px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">كيف كُشفت الحقيقة</div>
          {result.motive && (<div><span className="text-zinc-500">الدافع:</span> <span className="text-zinc-200">{result.motive}</span></div>)}
          {result.method && (<div><span className="text-zinc-500">الأسلوب:</span> <span className="text-zinc-200">{result.method}</span></div>)}
          {result.key_contradiction && (<div><span className="text-zinc-500">التناقض الحاسم:</span> <span className="text-zinc-200">{result.key_contradiction}</span></div>)}
          {(result.primary_evidence_name || result.secondary_evidence_name) && (
            <div>
              <div className="text-zinc-500 mb-1">الأدلة الحاسمة:</div>
              <ul className="text-zinc-200 list-disc pr-5 space-y-0.5">
                {result.primary_evidence_name && <li>{result.primary_evidence_name}</li>}
                {result.secondary_evidence_name && <li>{result.secondary_evidence_name}</li>}
              </ul>
            </div>
          )}
        </div>
      )}
      {result.explanation && (<div className="mt-3 text-sm text-zinc-300 leading-relaxed">{result.explanation}</div>)}
      {Array.isArray(result.scoreboard) && result.scoreboard.length > 0 && (
        <div className="mt-4 border-t border-zinc-800/60 pt-3" data-testid="squad-result-scoreboard-wrap">
          <div className="text-[10px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar mb-2">ترتيب محققي الفرقة</div>
          <SessionScoreboard scoreboard={result.scoreboard} mvpIds={result.mvp_ids || []} compact />
        </div>
      )}
      <div className="text-[10px] text-zinc-500 mt-4 text-center">
        نقاط الفرقة داخلية · لا تؤثر على XP أو الرتب العالمية
      </div>
    </Panel>
  );
}

function VoteWidget({ sessionId, snap, caseSnap, canVote, onRefresh }) {
  const [tally, setTally] = useState({});
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const s = snap?.session;

  useEffect(() => {
    if (!s?.vote_open) return;
    (async () => {
      try {
        const c = squadClient(sessionId);
        const { data } = await c.get(`/sessions/${sessionId}/vote/tally`);
        setTally(data?.tally || {});
      } catch {}
    })();
  }, [sessionId, s?.vote_open, snap]);

  const cast = async () => {
    if (!chosen) { toast.error("اختر المشتبه به"); return; }
    setBusy(true);
    try {
      const c = squadClient(sessionId);
      const { data } = await c.post(`/sessions/${sessionId}/vote/cast`, { target_id: chosen });
      setTally(data?.tally || {});
      toast.success("تم تسجيل صوتك");
      onRefresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "فشل التصويت");
    } finally { setBusy(false); }
  };

  if (!s?.vote_open) return null;
  const total = Object.values(tally).reduce((a, b) => a + b, 0);
  return (
    <Panel data-testid="squad-vote-panel" className="border-amber-500/40">
      <div className="font-bold mb-2 text-amber-300">التصويت: {s.vote_prompt || "من المشتبه به؟"}</div>
      <div className="space-y-2 mb-3">
        {(caseSnap?.suspects || []).map((sus) => {
          const cnt = tally[sus.id] || 0;
          const pct = total ? Math.round((cnt / total) * 100) : 0;
          return (
            <label key={sus.id} className="block" data-testid={`squad-vote-option-${sus.id}`}>
              <div className="flex items-center gap-2 p-2 rounded bg-zinc-900">
                {canVote && <input type="radio" name="vote" value={sus.id} checked={chosen === sus.id} onChange={(e) => setChosen(e.target.value)} />}
                <span className="flex-1">{sus.name}</span>
                <span className="text-xs text-zinc-400">{cnt} ({pct}%)</span>
              </div>
              <div className="h-1 bg-zinc-800 rounded mt-1 overflow-hidden">
                <div className="h-1 bg-amber-500" style={{ width: `${pct}%` }} />
              </div>
            </label>
          );
        })}
      </div>
      {canVote && (
        <PrimaryBtn data-testid="squad-vote-submit" onClick={cast} disabled={busy || !chosen}>
          {busy ? "..." : "تسجيل التصويت"}
        </PrimaryBtn>
      )}
    </Panel>
  );
}

export function SquadRoom() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [tokenRec] = useState(getSquadToken(sid));
  const { snap, caseSnap, err, refresh } = useSquadPoll(sid);

  useEffect(() => {
    if (!tokenRec) {
      toast.error("لا يوجد رمز وصول للجلسة — يرجى الانضمام من جديد");
      nav("/squad");
    }
  }, [tokenRec, nav]);

  const role = snap?.role || tokenRec?.role;
  const s = snap?.session;
  const me = snap?.me;
  const members = snap?.members || [];
  const leaderPid = members.find(m => m.role === "leader")?.participant_id;

  const kick = async (pid) => {
    if (!window.confirm("إزالة هذا العضو من الفرقة؟")) return;
    try {
      const c = squadClient(sid);
      await c.post(`/sessions/${sid}/kick`, { participant_id: pid });
      toast.success("تمت الإزالة");
      refresh();
    } catch (e) { toast.error("فشل"); }
  };

  const leave = () => {
    clearSquadToken(sid);
    nav("/squad");
  };

  if (!tokenRec || err === 404 || err === 403) {
    return (
      <SquadShell title="فرقة التحقيق" back="/squad">
        <Panel>
          <div className="text-red-400 font-bold mb-2">لا يمكن الوصول إلى الجلسة</div>
          <p className="text-sm text-zinc-400 mb-4">قد تكون الجلسة قد انتهت أو تمت إزالتك.</p>
          <OutlineBtn onClick={leave}>العودة</OutlineBtn>
        </Panel>
      </SquadShell>
    );
  }

  if (!snap) return <SquadShell title="فرقة التحقيق"><Panel><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--gold,#C5A059)]" /></Panel></SquadShell>;

  const isLeader = role === "leader";
  const canVote = role === "leader" || role === "participant" || role === "guest";
  const state = s?.state;

  return (
    <SquadShell title={caseSnap?.title || "فرقة التحقيق"} back={null}>
      <div className="flex items-center justify-between">
        <StateBadge state={state} />
        <div className="text-xs text-zinc-500">أنت: {me?.display_name || "؟"} · {role === "leader" ? "قائد" : role === "guest" ? "ضيف" : "محقق"}</div>
      </div>

      {state === "LOBBY" && s?.join_code && (
        <QrPanel sessionId={sid} joinCode={s.join_code} />
      )}

      {/* Case Introduction — always visible during ACTIVE so squad members
          know WHICH case they are investigating. Fixes the "starts at suspects"
          gameplay gap: shows title / cover / description / date+location. */}
      {state !== "LOBBY" && caseSnap && (
        <Panel data-testid="squad-case-intro">
          {(caseSnap.cover || !caseSnap.description) && (
            <div className="relative w-full h-40 md:h-56 rounded-lg mb-3 overflow-hidden bg-gradient-to-br from-zinc-900 via-black to-zinc-950 border border-zinc-800 grid place-items-center" data-testid="squad-case-cover-wrap">
              {caseSnap.cover && (
                <img
                  src={assetUrl(caseSnap.cover)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="squad-case-cover"
                />
              )}
              <div className="relative text-center px-4">
                <div className="text-[10px] tracking-[0.5em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">OK FBI · CASE FILE</div>
                <div className="text-2xl font-black text-[color:var(--gold,#C5A059)] mt-1 font-mono-ar">{caseSnap.code || "—"}</div>
              </div>
            </div>
          )}
          <div className="flex items-baseline gap-2 mb-1">
            <div className="text-[color:var(--gold,#C5A059)] text-xs tracking-widest font-mono-ar">{caseSnap.code || "قضية"}</div>
            {caseSnap.difficulty && <div className="text-xs text-zinc-500">· {caseSnap.difficulty}</div>}
            {caseSnap.type && <div className="text-xs text-zinc-500">· {caseSnap.type}</div>}
          </div>
          <div className="text-xl font-black">{caseSnap.title || "القضية"}</div>
          {caseSnap.subtitle && (
            <div className="text-sm text-zinc-400 mt-1">{caseSnap.subtitle}</div>
          )}
          {(caseSnap.location || caseSnap.date || caseSnap.time) && (
            <div className="text-xs text-zinc-500 mt-2 font-mono-ar">
              {caseSnap.location}{caseSnap.date ? ` · ${caseSnap.date}` : ""}{caseSnap.time ? ` · ${caseSnap.time}` : ""}
            </div>
          )}
          {/* Story body — the heart of the INTRO stage. */}
          {caseSnap.description && (
            <>
              <div className="mt-4 text-[10px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">ماذا حدث؟</div>
              <p className="text-sm text-zinc-200 leading-loose mt-1 whitespace-pre-line" data-testid="squad-case-story">
                {caseSnap.description}
              </p>
            </>
          )}
          {caseSnap.scene && caseSnap.scene !== caseSnap.cover && (
            <img
              src={assetUrl(caseSnap.scene)}
              alt=""
              className="w-full max-h-64 object-cover rounded mt-3"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="squad-case-scene"
            />
          )}
          {/* Victim brief (built-in cases) — safe: no culprit info leaked. */}
          {caseSnap.victim && (caseSnap.victim.name || caseSnap.victim.background) && (
            <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3" data-testid="squad-case-victim">
              <div className="text-[10px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar mb-1">الضحية</div>
              <div className="text-sm">
                <span className="font-bold">{caseSnap.victim.name || "غير معروف"}</span>
                {caseSnap.victim.age ? <span className="text-zinc-500"> · {caseSnap.victim.age}</span> : null}
                {caseSnap.victim.job ? <span className="text-zinc-500"> · {caseSnap.victim.job}</span> : null}
              </div>
              {caseSnap.victim.background && (
                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{caseSnap.victim.background}</p>
              )}
              {Array.isArray(caseSnap.victim.last_moves) && caseSnap.victim.last_moves.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] tracking-widest text-zinc-500 mb-1">آخر تحركات</div>
                  <ul className="text-xs text-zinc-300 space-y-1">
                    {caseSnap.victim.last_moves.map((m, i) => (
                      <li key={i}><span className="text-[color:var(--gold,#C5A059)]">•</span> {m}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Panel>
      )}

      <Panel>
        <div className="text-sm font-bold mb-2">أعضاء الفرقة ({members.length}/{snap.max_members})</div>
        <MembersList members={members} role={role} leaderPid={leaderPid} onKick={kick} />
      </Panel>

      {state === "COMPLETED" && s?.result && (
        <ResultsPanel result={s.result} />
      )}

      {state === "ACTIVE" && caseSnap && (() => {
        // Progressive investigation ladder. Each stage reveals ONE section so
        // players don't jump straight to suspects/vote. Leader advances via
        // the sticky bottom bar (LeaderControls).
        const stage = s?.sub_stage || "INTRO";
        const showEvidence = ["EVIDENCE","SUSPECTS","INTERROGATION","VOTING","ACCUSING"].includes(stage);
        const showSuspects = ["SUSPECTS","INTERROGATION","VOTING","ACCUSING"].includes(stage);
        const showInterrogation = ["INTERROGATION","VOTING","ACCUSING"].includes(stage);
        const showVote     = ["VOTING","ACCUSING"].includes(stage);
        // Evidence ID → Evidence entry map (from the SAME sanitized snapshot the
        // server already sent; locked entries will still show as pin_locked with
        // no description/image so the confrontation panel degrades safely).
        const evidenceById = {};
        for (const _ev of (caseSnap.evidence || [])) {
          if (_ev && _ev.id) evidenceById[_ev.id] = _ev;
        }
        return (
          <>
            <Panel data-testid="squad-stage-indicator">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] text-[color:var(--gold,#C5A059)]/80 font-mono-ar">المرحلة</div>
                <div className="text-sm font-bold" data-testid="squad-stage-label">
                  {stage === "INTRO" && "مقدمة القضية"}
                  {stage === "EVIDENCE" && "استعراض الأدلة"}
                  {stage === "SUSPECTS" && "المشتبه بهم"}
                  {stage === "INTERROGATION" && "الاستجواب"}
                  {stage === "VOTING" && "التصويت"}
                  {stage === "ACCUSING" && "الاتهام النهائي"}
                </div>
              </div>
            </Panel>

            {showVote && (
              <VoteWidget sessionId={sid} snap={snap} caseSnap={caseSnap} canVote={canVote} onRefresh={refresh} />
            )}

            {showEvidence && (
              <Panel data-testid="squad-evidence-panel">
                <div className="text-sm font-bold mb-2">الأدلة</div>
                <div className="space-y-2">
                  {(caseSnap.evidence || []).map((ev) => (
                    <EvidenceItem key={ev.id} ev={ev} sessionId={sid} role={role} onUnlocked={refresh} disabledUnlock={false} />
                  ))}
                </div>
              </Panel>
            )}

            {showSuspects && !showInterrogation && (
              <Panel data-testid="squad-suspects-panel">
                <div className="text-sm font-bold mb-3">المشتبه بهم</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(caseSnap.suspects || []).map((sus) => (
                    <div
                      key={sus.id}
                      className="group rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 flex items-start gap-3 transition-all duration-200 hover:border-[color:var(--gold,#C5A059)]/40 hover:bg-zinc-900 hover:-translate-y-0.5 hover:shadow-[0_4px_20px_-8px_rgba(197,160,89,0.35)]"
                      data-testid={`squad-suspect-${sus.id}`}
                    >
                      <SuspectAvatar src={sus.portrait} size="md" testid={`squad-suspect-${sus.id}-avatar`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">مشتبه به</div>
                        <div className="font-bold text-base leading-tight mt-0.5 text-zinc-50 truncate" title={sus.name}>{sus.name}</div>
                        {(sus.job || sus.age) && (
                          <div className="text-xs text-zinc-400 mt-1">
                            {sus.job || ""}{sus.job && sus.age ? " · " : ""}{sus.age ? `${sus.age}` : ""}
                          </div>
                        )}
                        {sus.relation && (
                          <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed line-clamp-3">
                            <span className="text-zinc-600">علاقة بالضحية:</span> <span className="text-zinc-300">{sus.relation}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {showInterrogation && (
              <Panel data-testid="squad-interrogation-panel">
                <div className="text-sm font-bold mb-2 flex items-center gap-2">
                  <span>جلسات الاستجواب</span>
                  <span className="text-[10px] text-zinc-500">— اضغط على اسم المشتبه لفتح استجوابه</span>
                </div>
                <div className="space-y-2">
                  {(caseSnap.suspects || []).map((sus) => (
                    <details key={sus.id} className="rounded-lg border border-zinc-800 bg-zinc-900/50 transition-colors hover:border-[color:var(--gold,#C5A059)]/40" data-testid={`squad-interrogation-${sus.id}`}>
                      <summary className="p-3 cursor-pointer flex items-start gap-3 hover:bg-zinc-900/70 rounded-t-lg">
                        <SuspectAvatar src={sus.portrait} size="md" testid={`squad-interrogation-${sus.id}-avatar`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] tracking-[0.35em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">جلسة استجواب</div>
                          <div className="font-bold text-base leading-tight mt-0.5 text-zinc-50 truncate" title={sus.name}>{sus.name}</div>
                          {(sus.job || sus.age) && (
                            <div className="text-xs text-zinc-400 mt-1">
                              {sus.job || ""}{sus.job && sus.age ? " · " : ""}{sus.age ? `${sus.age}` : ""}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] text-[color:var(--gold,#C5A059)] tracking-widest self-center shrink-0">فتح</span>
                      </summary>
                      <div className="p-3 pt-0 space-y-3 border-t border-zinc-800/60">
                        <div className="text-[10px] tracking-[0.4em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar mt-3">جلسة استجواب — {sus.name}</div>
                        {sus.relation && (
                          <div className="text-xs text-zinc-400">
                            <span className="text-zinc-500">علاقته بالضحية:</span> <span className="text-zinc-200">{sus.relation}</span>
                          </div>
                        )}
                        {sus.motive && (
                          <div className="text-xs text-zinc-400">
                            <span className="text-zinc-500">الدافع المحتمل:</span> <span className="text-zinc-200">{sus.motive}</span>
                          </div>
                        )}
                        {sus.alibi && (sus.alibi.claim || sus.alibi.status) && (
                          <div className="rounded border border-amber-500/30 bg-amber-950/20 p-2">
                            <div className="text-[10px] text-amber-300 tracking-widest mb-1">الحجّة (Alibi)</div>
                            {sus.alibi.claim && <div className="text-sm text-zinc-200 leading-relaxed">{sus.alibi.claim}</div>}
                            {sus.alibi.status && (
                              <div className="text-[10px] text-amber-200 mt-1">
                                <span className="text-amber-300/70">حالة الحجة:</span> {translateAlibiStatus(sus.alibi.status)}
                              </div>
                            )}
                          </div>
                        )}
                        {Array.isArray(sus.timeline) && sus.timeline.length > 0 && (
                          <div>
                            <div className="text-[10px] tracking-widest text-zinc-500 mb-1">تحركات المشتبه</div>
                            <ol className="text-xs text-zinc-300 space-y-1 border-r border-zinc-800/60 pr-3" data-testid={`squad-interrogation-timeline-${sus.id}`}>
                              {sus.timeline.map((t, i) => {
                                const f = fmtTimelineEntry(t);
                                return (
                                  <li key={i} className="relative">
                                    <span className="absolute -right-[7px] top-1.5 w-2 h-2 rounded-full bg-[color:var(--gold,#C5A059)]" aria-hidden />
                                    {f.time && <span className="text-[color:var(--gold,#C5A059)] font-bold">{f.time}</span>}
                                    {f.time && f.event && <span className="text-zinc-500"> — </span>}
                                    <span className="text-zinc-200">{f.event}</span>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        )}
                        {sus.secret && (
                          <div className="text-xs text-zinc-400 border-t border-zinc-800/40 pt-2">
                            <span className="text-zinc-500">ملاحظة المحقق:</span> <span className="text-zinc-200">{sus.secret}</span>
                          </div>
                        )}
                        {Array.isArray(caseSnap.interrogation_questions) && caseSnap.interrogation_questions.length > 0 && (
                          <div className="border-t border-zinc-800/40 pt-3">
                            <div className="text-[10px] tracking-widest text-zinc-500 mb-2">أسئلة الاستجواب</div>
                            <div className="space-y-2" data-testid={`squad-interrogation-questions-${sus.id}`}>
                              {caseSnap.interrogation_questions.map((q, i) => {
                                const text = q.q || q.question || (typeof q === "string" ? q : "");
                                const confrontId = q.confront || q.evidence_id || null;
                                const confrontEv = confrontId ? evidenceById[confrontId] : null;
                                return (
                                  <QuestionCard
                                    key={i}
                                    index={i}
                                    question={text}
                                    confrontEv={confrontEv}
                                    testidBase={`squad-interrogation-q-${sus.id}`}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              </Panel>
            )}

            {showEvidence && (caseSnap.timeline || []).length > 0 && (
              <Panel>
                <div className="text-sm font-bold mb-2">الخط الزمني</div>
                <ul className="text-sm text-zinc-300 space-y-1">
                  {caseSnap.timeline.map((t, i) => (
                    <li key={i}><span className="text-[color:var(--gold,#C5A059)]">•</span> {t.at} — {t.event}</li>
                  ))}
                </ul>
              </Panel>
            )}
          </>
        );
      })()}

      {isLeader && state !== "COMPLETED" && state !== "CLOSED" && (
        <div className="fixed bottom-0 inset-x-0 z-20 border-t border-[color:var(--gold,#C5A059)]/30 bg-black/90 backdrop-blur p-3 space-y-2 max-w-3xl mx-auto">
          <LeaderControls sessionId={sid} snap={snap} caseSnap={caseSnap} onRefresh={refresh} />
        </div>
      )}
      {!isLeader && (
        <OutlineBtn data-testid="squad-leave-btn" onClick={leave}>
          <LogOut className="w-4 h-4 inline ml-2" /> مغادرة الفرقة
        </OutlineBtn>
      )}
    </SquadShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// SquadDisplay — Desktop / TV 16:9 view-only
// ═══════════════════════════════════════════════════════════════
export function SquadDisplay() {
  const { sid } = useParams();
  const [params] = useSearchParams();
  const dt = params.get("dt") || "";
  const [snap, setSnap] = useState(null);
  const [caseSnap, setCaseSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [tally, setTally] = useState({});

  useEffect(() => {
    if (!dt) { setErr("رمز الشاشة مفقود"); return; }
    // Persist the display token for this browser so refresh keeps it working.
    try { saveSquadToken(sid, dt, "display", `display-${sid.slice(0,8)}`); } catch {}
  }, [dt, sid]);

  const refresh = useCallback(async () => {
    try {
      const c = squadClient(sid);
      const [s, cs] = await Promise.all([
        c.get(`/sessions/${sid}`).then(r => r.data),
        c.get(`/sessions/${sid}/case`).then(r => r.data).catch(() => null),
      ]);
      setSnap(s);
      setCaseSnap(cs);
      if (s?.session?.vote_open) {
        try {
          const { data } = await c.get(`/sessions/${sid}/vote/tally`);
          setTally(data?.tally || {});
        } catch {}
      }
      setErr(null);
    } catch (e) {
      setErr(e?.response?.status || "error");
    }
  }, [sid]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [refresh]);

  if (err) {
    return (
      <div dir="rtl" className="min-h-screen grid place-items-center bg-black text-zinc-300 font-cairo">
        <div className="text-center">
          <ShieldAlert className="w-14 h-14 text-red-500 mx-auto mb-3" />
          <div className="text-2xl font-bold text-red-300">لا يمكن عرض هذه الجلسة</div>
          <div className="text-sm text-zinc-500 mt-2">تأكد من صحة الرابط أو أعد ربط الشاشة من جهاز القائد</div>
        </div>
      </div>
    );
  }

  const s = snap?.session;
  const state = s?.state;

  return (
    <div dir="rtl" className="min-h-screen bg-black text-zinc-100 font-cairo" data-testid="squad-display-root">
      <header className="border-b border-[color:var(--gold,#C5A059)]/25 bg-gradient-to-l from-black to-[#100c05] px-8 py-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] tracking-[0.5em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">OK FBI · SQUAD DISPLAY</div>
          <div className="text-3xl font-black">{caseSnap?.title || "فرقة التحقيق"}</div>
        </div>
        <div className="text-left">
          <StateBadge state={state} />
          <div className="text-xs text-zinc-400 mt-1">رمز: <span className="text-[color:var(--gold,#C5A059)] font-mono-ar tracking-[0.3em]">{s?.join_code || "—"}</span></div>
        </div>
      </header>

      {state === "LOBBY" && s?.join_code && (
        <div className="p-10 grid grid-cols-2 gap-10 items-center">
          <div className="bg-white p-4 rounded-2xl inline-block mx-auto">
            <QRCodeSVG value={`${window.location.origin}/squad/join?code=${s.join_code}`} size={280} level="M" />
          </div>
          <div>
            <div className="text-xl text-zinc-400 mb-2">امسح الرمز أو أدخل يدوياً</div>
            <div className="text-[10rem] font-black tracking-[0.15em] text-[color:var(--gold,#C5A059)] leading-none" data-testid="squad-display-code">{s.join_code}</div>
            <div className="text-xl text-zinc-400 mt-3">الأعضاء: {snap.members?.length || 0} / {snap.max_members}</div>
            <div className="mt-6 space-y-1">
              {(snap.members || []).map((m) => (
                <div key={m.participant_id} className="text-lg">
                  <span className="text-[color:var(--gold,#C5A059)]">•</span> {m.display_name}
                  <span className="text-xs text-zinc-500 mr-2">({m.role === "leader" ? "قائد" : m.role === "guest" ? "ضيف" : "محقق"})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(state === "ACTIVE" || state === "FINAL_VOTE") && caseSnap && (() => {
        const stage = s?.sub_stage || "INTRO";
        const showEvidence = ["EVIDENCE","SUSPECTS","INTERROGATION","VOTING","ACCUSING"].includes(stage);
        const showSuspects = ["SUSPECTS","INTERROGATION","VOTING","ACCUSING"].includes(stage);
        const showInterrogation = ["INTERROGATION","VOTING","ACCUSING"].includes(stage);
        return (
        <div className="p-8 space-y-6">
          {stage === "ACCUSING" && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-950/10 p-6 text-center" data-testid="squad-display-accusing-banner">
              <div className="text-[11px] tracking-[0.5em] text-amber-300 font-mono-ar">قرار نهائي</div>
              <div className="text-3xl font-black text-zinc-50 mt-2">الفرقة تناقش الاتهام النهائي</div>
              <div className="text-sm text-zinc-400 mt-2">القائد يعتمد القرار من الجهاز — النتيجة ستُكشف بعد التأكيد.</div>
            </div>
          )}
          <div className="grid grid-cols-[auto_1fr] gap-6 items-start">
            {caseSnap.cover && (
              <img src={assetUrl(caseSnap.cover)} alt="" className="w-64 h-40 object-cover rounded-xl" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="squad-display-cover" />
            )}
            <div>
              <div className="text-sm text-[color:var(--gold,#C5A059)] tracking-widest">{caseSnap.code}{caseSnap.difficulty ? ` · ${caseSnap.difficulty}` : ""}{caseSnap.type ? ` · ${caseSnap.type}` : ""}</div>
              <div className="text-3xl font-black">{caseSnap.title}</div>
              {caseSnap.subtitle && <div className="text-lg text-zinc-400 mt-1">{caseSnap.subtitle}</div>}
              {(caseSnap.location || caseSnap.date) && (
                <div className="text-sm text-zinc-500 mt-1 font-mono-ar">{caseSnap.location}{caseSnap.date ? ` · ${caseSnap.date}` : ""}{caseSnap.time ? ` · ${caseSnap.time}` : ""}</div>
              )}
              {caseSnap.description && (
                <>
                  <div className="mt-4 text-[11px] tracking-[0.4em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">ماذا حدث؟</div>
                  <p className="text-base text-zinc-200 mt-2 leading-loose whitespace-pre-line max-w-4xl">{caseSnap.description}</p>
                </>
              )}
              <div className="inline-block mt-3 px-3 py-1 rounded border border-[color:var(--gold,#C5A059)]/40 text-xs text-[color:var(--gold,#C5A059)] tracking-widest" data-testid="squad-display-stage">
                {stage === "INTRO" && "مقدمة القضية"}
                {stage === "EVIDENCE" && "استعراض الأدلة"}
                {stage === "SUSPECTS" && "المشتبه بهم"}
                {stage === "INTERROGATION" && "الاستجواب"}
                {stage === "VOTING" && "التصويت"}
                {stage === "ACCUSING" && "الاتهام النهائي"}
              </div>
            </div>
          </div>
          {(showEvidence || showSuspects) && (
          <div className="grid grid-cols-[1fr_1fr] gap-8">
          {showSuspects ? (
          <div>
            <h2 className="text-2xl font-bold text-[color:var(--gold,#C5A059)] mb-3">{showInterrogation ? "جلسات الاستجواب" : "المشتبه بهم"}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {(caseSnap.suspects || []).map((sus) => (
                <div key={sus.id} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 flex flex-col gap-2">
                  <SuspectAvatar src={sus.portrait} size="lg" />
                  <div className="text-[10px] tracking-[0.4em] text-[color:var(--gold,#C5A059)]/70 font-mono-ar">مشتبه به</div>
                  <div className="font-bold text-xl leading-tight text-zinc-50">{sus.name}</div>
                  {(sus.job || sus.age) && (
                    <div className="text-sm text-zinc-400">
                      {sus.job || ""}{sus.job && sus.age ? " · " : ""}{sus.age ? `${sus.age}` : ""}
                    </div>
                  )}
                  {showInterrogation && sus.relation && (
                    <div className="text-xs text-zinc-500 leading-relaxed">علاقة: {sus.relation}</div>
                  )}
                  {showInterrogation && sus.alibi?.claim && (
                    <div className="mt-1 rounded border border-amber-500/30 bg-amber-950/20 p-2">
                      <div className="text-[10px] text-amber-300 tracking-widest">الحجّة</div>
                      <div className="text-xs text-zinc-200 mt-1">{sus.alibi.claim}</div>
                      {sus.alibi.status && <div className="text-[10px] text-amber-200 mt-1">الحالة: {translateAlibiStatus(sus.alibi.status)}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          ) : <div />}
          {showEvidence ? (
          <div>
            <h2 className="text-2xl font-bold text-[color:var(--gold,#C5A059)] mb-3">الأدلة</h2>
            <div className="space-y-2">
              {(caseSnap.evidence || []).map((ev) => (
                <div key={ev.id} className={`rounded-lg border p-3 ${ev.pin_locked && !ev.description ? "border-amber-600/40 bg-amber-950/20" : "border-zinc-800 bg-zinc-900/50"}`}>
                  <div className="flex items-center gap-2">
                    {ev.pin_locked && !ev.description ? <Lock className="w-4 h-4 text-amber-400" /> : ev.pin_locked ? <Unlock className="w-4 h-4 text-emerald-400" /> : null}
                    <div className="font-bold">{ev.name}</div>
                  </div>
                  {ev.description && <div className="text-sm text-zinc-300 mt-1">{ev.description}</div>}
                  {ev.image && (
                    <img
                      alt=""
                      src={assetUrl(ev.image)}
                      loading="lazy"
                      className="mt-2 rounded max-h-40 object-cover w-full"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  {!ev.description && ev.pin_locked && ev.pin_hint && (
                    <div className="text-xs text-amber-300 mt-1">تلميح: {ev.pin_hint}</div>
                  )}
                </div>
              ))}
            </div>
            {s?.vote_open && (
              <div className="mt-4 p-4 rounded-lg border border-amber-500/40 bg-amber-950/20">
                <div className="text-lg font-bold text-amber-300 mb-2">التصويت جارٍ: {s.vote_prompt}</div>
                {(caseSnap.suspects || []).map((sus) => {
                  const total = Object.values(tally).reduce((a, b) => a + b, 0) || 1;
                  const cnt = tally[sus.id] || 0;
                  const pct = Math.round((cnt / total) * 100);
                  return (
                    <div key={sus.id} className="mb-2">
                      <div className="flex justify-between text-sm mb-1"><span>{sus.name}</span><span>{cnt}</span></div>
                      <div className="h-2 bg-zinc-800 rounded"><div className="h-2 bg-amber-500 rounded" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          ) : <div />}
        </div>
        )}
        </div>
        );
      })()}

      {state === "COMPLETED" && s?.result && (
        <div className="p-12 max-w-6xl mx-auto" data-testid="squad-display-result">
          <div className="text-center mb-8">
            {s.result.solved ? (
              <>
                <Trophy className="w-24 h-24 text-[color:var(--gold,#C5A059)] mx-auto mb-4" />
                <div className="text-5xl font-black text-emerald-300">قرار صحيح</div>
              </>
            ) : (
              <>
                <XCircle className="w-24 h-24 text-red-500 mx-auto mb-4" />
                <div className="text-5xl font-black text-red-300">قرار غير صحيح</div>
              </>
            )}
          </div>
          <div className={`grid ${s.result.solved ? "grid-cols-1 max-w-md mx-auto" : "grid-cols-2 max-w-3xl mx-auto"} gap-6`}>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 text-center" data-testid="squad-display-result-chosen">
              <div className="text-[11px] tracking-[0.4em] text-[color:var(--gold,#C5A059)]/80 font-mono-ar mb-3">اختيار الفرقة</div>
              <SuspectAvatar src={s.result.chosen_suspect_portrait} size="lg" />
              <div className="text-2xl font-bold text-zinc-50 mt-3" data-testid="squad-display-result-chosen-name">{s.result.chosen_suspect_name || "—"}</div>
              {s.result.chosen_suspect_job && <div className="text-sm text-zinc-400 mt-1">{s.result.chosen_suspect_job}</div>}
            </div>
            {!s.result.solved && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/10 p-4 text-center" data-testid="squad-display-result-correct">
                <div className="text-[11px] tracking-[0.4em] text-emerald-300 font-mono-ar mb-3">المشتبه الصحيح</div>
                <SuspectAvatar src={s.result.correct_suspect_portrait} size="lg" />
                <div className="text-2xl font-bold text-zinc-50 mt-3" data-testid="squad-display-result-correct-name">{s.result.correct_suspect_name || "—"}</div>
                {s.result.correct_suspect_job && <div className="text-sm text-zinc-400 mt-1">{s.result.correct_suspect_job}</div>}
              </div>
            )}
          </div>
          <div className="text-center mt-10">
            <div className="text-7xl font-black text-[color:var(--gold,#C5A059)]">{s.result.squad_score}</div>
            <div className="text-sm text-zinc-500 tracking-widest mt-1">نقاط الفرقة · مدة التحقيق {fmtDuration(s.result.elapsed_seconds)}</div>
          </div>
          {Array.isArray(s.result.scoreboard) && s.result.scoreboard.length > 0 && (
            <div className="mt-8 max-w-2xl mx-auto" data-testid="squad-display-scoreboard-wrap">
              <div className="text-lg tracking-[0.35em] text-[color:var(--gold,#C5A059)]/80 font-mono-ar text-center mb-3">ترتيب محققي الفرقة</div>
              <SessionScoreboard scoreboard={(s.result.scoreboard || []).slice(0, 5)} mvpIds={s.result.mvp_ids || []} testidBase="squad-display-scoreboard" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
